/**
 * Tug-E-Nuff Predictive Search — ten-search.js
 * Chris Nelis-Rouse — 25 Jun 2026
 * Clean build. Targets id="ten-search-bar" (desktop) and id="ten-search-bar-mobile".
 * No Searchanise dependency. No hacks.
 *
 * Chris Nelis-Rouse — 3 Jul 2026 (rev 3): removed the dim/backdrop overlay.
 * The panel is now a plain bordered dropdown anchored below the header search box.
 * Desktop: the header search box is the live field and the panel's duplicate input
 * row is hidden (see ten-search.css); header typing is mirrored into the panel input
 * so all existing search logic runs unchanged. Mobile: the panel's own input row is
 * used. Outside-click closes (there is no backdrop to catch it).
 */
(function () {
  'use strict';

  const CONFIG = {
    minChars:       2,
    debounce:       220,
    maxProducts:    5,
    maxCollections: 4,
    maxArticles:    4,
    maxSuggestions: 3,
    searchUrl:     '/search?q=',
    // Chris Nelis-Rouse — 6 Jul 2026: GA4 dropdown-search tracking.
    trackSearch:    true,              // log predictive-dropdown queries to GA4
    ga4Id:         'G-MZK3Q8CWDD',     // GA4 property these search events target
    trackMinChars:  3,                 // ignore very short/incidental queries
    trackDelay:     1000,              // ms of typing-idle before a settled query logs
  };

  function init() {
    const modal      = document.getElementById('ten-search-modal');
    const defaultEl  = document.getElementById('ten-search-default');
    const liveEl     = document.getElementById('ten-search-live');
    const loadingEl  = document.getElementById('ten-search-loading');
    const emptyEl    = document.getElementById('ten-search-empty');
    const emptyTerm  = document.getElementById('ten-search-empty-term');
    const tabsEl     = document.getElementById('ten-search-tabs');
    const suggestEl  = document.getElementById('ten-search-suggestions');
    const suggestList= document.getElementById('ten-search-suggestions-list');
    const prodList   = document.getElementById('ten-search-products');
    const artList    = document.getElementById('ten-search-articles');
    const viewAll    = document.getElementById('ten-search-all-products');
    const tabBtns    = document.querySelectorAll('.ten-search-tab');
    const tabPanels  = document.querySelectorAll('.ten-tab-panel');

    if (!modal) return;

    // Panel input — inside the modal panel (preferred)
    // Falls back to header search bar if snippet hasn't been updated yet
    const panelInput = document.getElementById('ten-panel-input');

    // Header trigger inputs
    const headerInputs = [
      document.getElementById('ten-search-bar'),
      document.getElementById('ten-search-bar-mobile'),
      document.querySelector('form.search input[name="q"]'),
      document.querySelector('form.search-mobile input[name="q"]'),
    ].filter((el, i, arr) => el && el !== panelInput && arr.indexOf(el) === i);

    // Primary input for search logic: panel input if available, else first header input
    const primaryInput = panelInput || headerInputs[0];
    if (!primaryInput) { console.warn('[TEN] No search input found'); return; }

    // Trigger inputs: header inputs always; panel input handled separately below
    const triggerInputs = headerInputs;

    let debounceTimer = null;
    let lastQuery     = '';
    let abortCtrl     = null;
    let isOpen        = false;

    /* ── DESKTOP INPUT MODE — Chris Nelis-Rouse, 3 Jul 2026 ──
       Desktop: the visible header input IS the live search field, so the panel's
       own input row is hidden (via .ten-search-modal--anchored in ten-search.css).
       Mobile: no visible header input → the panel's own input row is used. */
    function visibleTrigger() {
      if (window.innerWidth <= 749) return null;
      for (const el of triggerInputs) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;
      }
      return null;
    }

    function syncAnchored() {
      const trigger  = isOpen ? visibleTrigger() : null;
      const useHeader = !!(trigger && panelInput);
      modal.classList.toggle('ten-search-modal--anchored', useHeader);
      return useHeader ? trigger : null;
    }

    /* ── POSITION ── */
    function positionPanel() {
      const panel = modal.querySelector('.ten-search-modal__panel');
      if (!panel || window.innerWidth <= 749) return;
      // Anchor to the visible HEADER search field — never the panel's own input.
      // The panel input lives inside the panel, so anchoring to it (or its parent)
      // measures the panel's own position and feeds back on itself, drifting the
      // panel down and right on every open. Chris Nelis-Rouse — 3 Jul 2026
      const anchorInput = visibleTrigger() || headerInputs[0];
      if (!anchorInput) return;
      const anchor = anchorInput.closest('form') || anchorInput.closest('.search-block') || anchorInput.parentElement;
      const rect   = anchor.getBoundingClientRect();
      if (rect.width < 10) return;
      panel.style.top   = (rect.bottom + 4) + 'px';
      panel.style.left  = rect.left + 'px';
      panel.style.width = Math.max(rect.width, 560) + 'px';
    }

    /* ── OPEN / CLOSE ── */
    function openModal() {
      if (isOpen) return;
      isOpen = true;
      modal.hidden = false;
      // Lock scroll without the horizontal jump: hiding the scrollbar widens the
      // viewport, so pad the body by the scrollbar width to hold layout still.
      // Chris Nelis-Rouse — 3 Jul 2026
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      positionPanel();
      window._tenResize = () => { positionPanel(); syncAnchored(); };
      window.addEventListener('resize', window._tenResize);
      wireDefaultLinks();
      // Focus synchronously, inside the triggering user gesture. Deferring with
      // requestAnimationFrame pushes the focus() outside the gesture, which mobile
      // browsers ignore — the keyboard then closes after the first keystroke and
      // input stops. Chris Nelis-Rouse — 3 Jul 2026
      // Desktop: keep the visible header input focused. Mobile: focus the panel input.
      const anchorEl = syncAnchored();
      if (anchorEl) {
        anchorEl.focus();
      } else if (panelInput) {
        panelInput.focus();
      }
    }

    function closeModal() {
      if (!isOpen) return;
      isOpen = false;
      modal.hidden = true;
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      window.removeEventListener('resize', window._tenResize);
      modal.classList.remove('ten-search-modal--anchored');
      panelInput.value = '';
      triggerInputs.forEach(el => { el.value = ''; });
      lastQuery = '';
      showDefault();
    }

    function showDefault() {
      if (defaultEl) defaultEl.hidden = false;
      if (liveEl)    liveEl.hidden    = true;
    }
    function showLive() {
      if (defaultEl) defaultEl.hidden = true;
      if (liveEl)    liveEl.hidden    = false;
    }

    /* ── TABS ── */
    function switchTab(name) {
      tabBtns.forEach(b => b.classList.toggle('ten-search-tab--active', b.dataset.tab === name));
      tabPanels.forEach(p => {
        const active = p.id === `ten-tab-${name}`;
        p.hidden = !active;
        p.classList.toggle('ten-tab-panel--active', active);
      });
    }
    tabBtns.forEach(b => b.addEventListener('click', () => switchTab(b.dataset.tab)));

    /* ── HELPERS ── */
    function esc(s) {
      return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
    function hl(text, q) {
      if (!q || !text) return esc(text || '');
      return esc(text).replace(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')})`, 'gi'), '<mark>$1</mark>');
    }
    function thumb(src, alt) {
      if (src) return `<img class="ten-search-result__thumb" src="${esc(src.includes('?')?src+'&width=120':src+'?width=120')}" alt="${esc(alt)}" width="60" height="60" loading="lazy">`;
      return `<span class="ten-search-result__thumb-placeholder" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></span>`;
    }

    /* ── RENDER ── */
    function renderProduct(p, q) {
      // Chris Nelis-Rouse — 6 Jul 2026: prices intentionally not shown in the
      // predictive dropdown. Removed dead price code (read a non-existent
      // suggest.json field) and the hardcoded GBP/en-GB currency formatter.
      return `<li><a class="ten-search-result" href="${esc(p.url)}">${thumb(p.featured_image?.url, p.title)}<span class="ten-search-result__body"><span class="ten-search-result__title">${hl(p.title,q)}</span></span></a></li>`;
    }
    function renderCollCard(c, q) {
      const img = c.featured_image?.url ? `<img class="ten-collection-card__thumb" src="${esc(c.featured_image.url)}?width=120" alt="${esc(c.title)}" width="60" height="60" loading="lazy">` : `<span class="ten-collection-card__thumb ten-collection-card__thumb--placeholder"></span>`;
      return `<li class="ten-collection-card-item"><a class="ten-collection-card" href="${esc(c.url)}">${img}<span class="ten-collection-card__body"><span class="ten-collection-card__label">Collection</span><span class="ten-collection-card__title">${hl(c.title,q)}</span><span class="ten-collection-card__cta">Browse all →</span></span></a></li>`;
    }
    function renderArticle(a, q) {
      const imgUrl = a.featured_image?.url || a.image?.url || null;
      return `<li><a class="ten-search-result" href="${esc(a.url)}">${thumb(imgUrl, a.title)}<span class="ten-search-result__body"><span class="ten-search-result__title">${hl(a.title,q)}</span></span></a></li>`;
    }

    function renderResults(data, q) {
      const r    = data?.resources?.results || {};
      const prods = (r.products    || []).slice(0, CONFIG.maxProducts);
      const colls = (r.collections || []).slice(0, CONFIG.maxCollections);
      const arts  = (r.articles    || []).slice(0, CONFIG.maxArticles);
      const qrys  = (r.queries     || []).slice(0, CONFIG.maxSuggestions);
      const hasP  = prods.length > 0;
      const hasA  = arts.length > 0;
      const hasAny= hasP || colls.length > 0 || hasA;

      if (loadingEl) loadingEl.hidden = true;

      // Suggestions
      if (qrys.length) {
        suggestList.innerHTML = qrys.map(sq =>
          `<li><a href="${CONFIG.searchUrl}${encodeURIComponent(sq.text)}" class="ten-suggestion-link" data-term="${esc(sq.text)}">${sq.styled_text || esc(sq.text)}</a></li>`
        ).join('');
        suggestList.querySelectorAll('.ten-suggestion-link').forEach(a => {
          a.addEventListener('click', e => { e.preventDefault(); triggerSearch(a.dataset.term); });
        });
        suggestEl.hidden = false;
      } else {
        suggestEl.hidden = true;
      }

      const hasColl = colls.length > 0;
      const hasToys = hasP || hasColl;   // collections live in the Toys tab

      if (tabsEl) tabsEl.hidden = !hasAny;
      // Collections take priority — render their cards above products in the Toys tab
      if (prodList) prodList.innerHTML = colls.map(c => renderCollCard(c, q)).join('') + prods.map(p => renderProduct(p, q)).join('');
      if (artList)  artList.innerHTML  = arts.map(a => renderArticle(a, q)).join('');
      if (viewAll)  { viewAll.href = CONFIG.searchUrl + encodeURIComponent(q); viewAll.hidden = !hasP; }
      if (emptyEl)  emptyEl.hidden = hasAny || qrys.length > 0;
      if (!hasAny && !qrys.length && emptyTerm) emptyTerm.textContent = q;
      // Default to the Toys tab whenever it has anything (products OR a collection);
      // only fall back to Articles when there are no toys to show.
      if (!hasToys && hasA) switchTab('articles');
      else switchTab('products');
      showLive();

      // GA4: log the query once typing settles (idle timer, deduped per session).
      if (CONFIG.trackSearch) {
        lastResultsCount = prods.length + colls.length + arts.length;
        clearTimeout(trackTimer);
        const settledTerm = q;
        trackTimer = setTimeout(() => trackSearchQuery(settledTerm), CONFIG.trackDelay);
      }
    }

    /* ── GA4 SEARCH TRACKING — Chris Nelis-Rouse, 6 Jul 2026 ──
       Shopify's Search & Discovery report only counts the /search results page and
       explicitly excludes predictive-dropdown activity. TEN search resolves most
       queries in the dropdown, so those searches never show there. Mirror them into
       GA4 as the standard `search` event so the full picture lives in Analytics.
       Fires once per distinct settled query; dedupe resets when the box is cleared. */
    let   trackTimer;
    let   lastResultsCount = 0;
    const sentSearches = new Set();

    function trackSearchQuery(term) {
      if (!CONFIG.trackSearch) return;
      const q = (term || '').trim();
      if (q.length < CONFIG.trackMinChars) return;
      const key = q.toLowerCase();
      if (sentSearches.has(key)) return;
      sentSearches.add(key);
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'search', {
            search_term:    q,
            search_results: lastResultsCount,   // 0 = a search that returned nothing
            search_source: 'ten_predictive',    // separates dropdown from /search hits
            send_to:        CONFIG.ga4Id,        // GA4 only — keep it out of Ads
          });
          // Dedicated no-results signal — Chris Nelis-Rouse, 6 Jul 2026.
          // Fires only when the settled query returned nothing, so "searches with
          // no results" is a one-click event report in GA4 (no custom dimension needed).
          if (lastResultsCount === 0) {
            window.gtag('event', 'search_no_results', {
              search_term:   q,
              search_source: 'ten_predictive',
              send_to:       CONFIG.ga4Id,
            });
          }
        }
      } catch (e) { /* analytics must never break search */ }
    }

    /* ── FETCH ── */
    async function fetchResults(q) {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      try {
        const url = `/search/suggest.json?${new URLSearchParams({ q })}`
          + '&resources[type]=product,collection,article,query'
          + '&resources[limit]=10';
        const res = await fetch(url, { signal: abortCtrl.signal });
        if (!res.ok) throw new Error(res.status);
        return res.json();
      } catch(e) {
        if (e.name !== 'AbortError') console.warn('[TEN]', e);
        return null;
      }
    }

    /* ── INPUT HANDLER ── */
    function onInput() {
      const q = primaryInput.value.trim();
      if (q.length === 0) {
        if (abortCtrl) abortCtrl.abort();
        clearTimeout(debounceTimer);
        clearTimeout(trackTimer);
        sentSearches.clear();
        showDefault();
        lastQuery = '';
        return;
      }
      if (q === lastQuery) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        if (q.length < CONFIG.minChars) return;
        lastQuery = q;
        if (loadingEl) loadingEl.hidden = false;
        showLive();
        const data = await fetchResults(q);
        if (data) renderResults(data, q);
      }, CONFIG.debounce);
    }

    /* ── TRIGGER SEARCH from links ── */
    function triggerSearch(term) {
      panelInput.value = term;
      // Anchored mode: the header box is the visible field — show the term there too
      const anchorEl = syncAnchored();
      if (anchorEl) anchorEl.value = term;
      lastQuery = '';
      onInput();
      (anchorEl || panelInput).focus();
    }

    /* ── DEFAULT LINK WIRING ── */
    function wireDefaultLinks() {
      document.querySelectorAll('#ten-search-default .ten-search-default__link').forEach(link => {
        if (link.dataset.tenWired) return;
        link.dataset.tenWired = '1';
        const href = link.getAttribute('href') || '';
        if (!href.includes('/search?q=')) return;
        link.addEventListener('click', e => {
          e.preventDefault();
          const params = new URLSearchParams(href.split('?')[1] || '');
          triggerSearch(params.get('q') || link.textContent.trim());
        });
      });
    }

    /* ── WIRE PANEL INPUT (typing) — only if panel input exists in snippet ── */
    if (panelInput) {
      panelInput.addEventListener('input', onInput);
      panelInput.addEventListener('keydown', e => {
        if (e.key === 'Escape') { closeModal(); }
      });
    }

    /* ── FLUSH GA4 SEARCH ON RESULT CLICK — Chris Nelis-Rouse, 6 Jul 2026 ──
       A fast tap on a suggestion can beat the idle timer, so log the current query
       immediately when a result inside the panel is clicked. gtag uses sendBeacon,
       so the event survives the page navigation that follows. */
    if (liveEl) {
      liveEl.addEventListener('click', e => {
        if (e.target && e.target.closest && e.target.closest('a')) {
          clearTimeout(trackTimer);
          trackSearchQuery(lastQuery);
        }
      }, true);
    }

    /* ── WIRE TRIGGER INPUTS (header search bars) ── */
    triggerInputs.forEach(el => {
      el.addEventListener('focus', () => {
        if (panelInput && el.value) panelInput.value = el.value;
        openModal();
      });
      el.addEventListener('mousedown', () => {
        if (!isOpen) openModal();
      });
      // Typing in the header input drives search:
      //  - no panel input in snippet → header input IS primaryInput, wire directly
      //  - panel input present (anchored mode) → mirror value into the panel input
      //    so all existing logic (debounce, fetch, render) runs unchanged
      if (!panelInput) {
        el.addEventListener('input', () => { if (!isOpen) openModal(); onInput(); });
      } else {
        el.addEventListener('input', () => {
          if (!isOpen) openModal();
          panelInput.value = el.value;
          onInput();
        });
      }
      el.addEventListener('keydown', e => { if (e.key === 'Escape') { closeModal(); el.blur(); } });
      const form = el.closest('form');
      if (form) {
        form.addEventListener('submit', e => {
          const q = (panelInput?.value || el.value || '').trim();
          if (q) {
            e.preventDefault();
            clearTimeout(trackTimer);
            trackSearchQuery(q);
            window.location.href = CONFIG.searchUrl + encodeURIComponent(q);
          }
        });
      }
    });

    /* ── CANCEL BUTTON ── */
    const cancelBtn = document.getElementById('ten-search-cancel');
    if (cancelBtn) cancelBtn.addEventListener('click', closeModal);

    /* ── SEARCH BUTTONS → full-screen search — Chris Nelis-Rouse, 05 Jul 2026 ──
       The mobile search icon (and any .ten-search-open / #ten-mobile-search-trigger
       trigger) opens the full-screen panel and focuses the panel's own input INSIDE
       the tap gesture — no input-to-input focus handoff, so the mobile keyboard stays
       open. Replaces the theme's native #search-modal binding. */
document.querySelectorAll('.ten-search-open, #ten-mobile-search-trigger').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        // When the trigger sits inside the open menu drawer, focus.js has a
        // document-level focusin trap that pulls focus back into the drawer the
        // instant we focus the panel input — so the mobile keyboard never opens.
        // Release the trap and close the drawer synchronously, inside the tap
        // gesture, then open + focus. Chris Nelis-Rouse — 05 Jul 2026
        if (window.tenReleaseFocusTrap) window.tenReleaseFocusTrap();
        const drawerDetails = document.getElementById('Details-menu-drawer-container');
        const drawer = document.querySelector('header-drawer');
        if (drawer && drawerDetails && drawerDetails.hasAttribute('open') && typeof drawer.close === 'function') {
          drawer.close();
        }
        openModal();
        if (panelInput) panelInput.focus();
      });
    });

    document.addEventListener('keydown', e => { if (e.key === 'Escape' && isOpen) closeModal(); });
    window.addEventListener('popstate', () => { if (isOpen) closeModal(); });

    /* ── OUTSIDE CLICK CLOSES — no backdrop, root is click-through ── */
    document.addEventListener('click', e => {
      if (!isOpen) return;
      const t = e.target;
      if (t.closest && (
        t.closest('.ten-search-modal__panel') ||
        t.closest('.search-block') ||
        t.closest('form.search') ||
        t.closest('#ten-search-bar') ||
        t.closest('#ten-search-bar-mobile') ||
        t.closest('#ten-mobile-search-trigger') ||
        t.closest('.ten-search-open')
      )) return;
      closeModal();
    }, true);

    window.TENSearch = { open: openModal, close: closeModal, panelInput, triggerInputs };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();